import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";
import GlobalContext, { GlobalContextConfig } from "@/context/GlobalContext";
import { connect } from "dvajs";
import { UserState } from "@/models/user";
import styles from "./NavSetting.module.less";
import ThirdAppListModal from "@/baseComponents/ModalComponent/ThirdAppListModal";
import { getPcSideConfig, PC_SIDE_ITEM_MAX, PC_SIDE_ITEM_NUM, PC_SIDEBAR_KEY } from "@/utils/tools";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { get } from "lodash";
import SettingItem from "./SettingItem";
import Empty from "@/components/Supervise/components/empty";
import { EbookState } from "@/models/ebook";
import { syncConfirmNew } from "@/utils/syncConfirm";
import { ThirdAppState } from "@/models/thirdapp";
import { UpgradeTipTag } from "@/components/Tags";

const DEFAULT_LOCK: string[] = [];
/**
 *
 * 更多导航
 * @returns
 */
function NavSetting(props: any) {
  const {
    teamConfig,
    updateTeamConfig,
    navPopInfo,
    updateNavPopInfo,
    newContactsCount,
    thirdAppMsgCount,
    thirdAppMsgTotalCount,
    pcFuncInfoMap
  } = props;
  const { appRuntimeEnv } = useContext<GlobalContextConfig>(GlobalContext);
  const [isDragging, setDragging] = useState(false);

  // 关闭浮层
  const hidePop = useCallback(() => {
    updateNavPopInfo({
      visible: false
    });
  }, [updateNavPopInfo]);

  const updateNavInfo = useCallback(
    (data: any) => {
      updateNavPopInfo({
        ...data
      });
    },
    [updateNavPopInfo]
  );

  const needUpdate = useMemo(() => {
    const pcSider = getPcSideConfig(teamConfig);
    return pcSider?.needUpdate;
  }, [teamConfig]);

  const lockKeys = useMemo(() => {
    const pcSider = getPcSideConfig(teamConfig, {
      pKey: PC_SIDEBAR_KEY,
      getDefault: false
    });
    return pcSider?.lockDisRemovableScene || pcSider?.lockDisRemovable || DEFAULT_LOCK;
  }, [teamConfig]);

  const notRemoveKeys = useMemo(() => {
    const pcSider = getPcSideConfig(teamConfig, {
      pKey: PC_SIDEBAR_KEY,
      getDefault: false
    });
    return pcSider?.disRemovableScene || pcSider?.disRemovable || DEFAULT_LOCK;
  }, [teamConfig]);

  const getItemStyle = (isDragging: any, draggableStyle: any) => {
    return {
      userSelect: "none",
      boxShadow: isDragging ? " 0 1px 9px 0 rgba(198,198,198,0.5)" : "",
      cursor: "move",
      ...draggableStyle
    };
  };

  // 显示数据
  // 所有基础导航
  const viewAllMainItem = useMemo(() => {
    let tNav = getPcSideConfig(teamConfig, {
      key: "items"
    });
    if (!tNav || !tNav.length) {
      tNav = [];
    }
    const navs = tNav.length ? tNav : appRuntimeEnv?.ui?.layout?.nav || [];
    const newNavs = navs.map((i: any) => {
      if (i.role === "ebook") {
        return {
          ...i,
          tip: newContactsCount
        };
      } else if (i.role === "thirdApp") {
        return {
          ...i,
          tip: thirdAppMsgTotalCount
        };
      } else {
        return i;
      }
    });
    return newNavs;
  }, [appRuntimeEnv?.ui?.layout?.nav, teamConfig, newContactsCount, thirdAppMsgTotalCount]);

  const viewOtherItems = useMemo(() => {
    // 大于
    return viewAllMainItem.slice(PC_SIDE_ITEM_NUM, PC_SIDE_ITEM_MAX);
  }, [viewAllMainItem]);

  const hasOthers = useMemo(() => {
    return [...viewOtherItems].length > 0;
  }, [viewOtherItems]);

  // 处理导航显示数据
  const handleNavItem = useCallback(
    (item: any, data: any) => {
      if (data) {
        item.cnName = data?.name;
        item.svg = data?.svg;
        item.avatar = data?.icon;
        if (data?.pcUrl) {
          item.pcUrl = data?.pcUrl;
          item.path = "/linkContainer";
          item.role = "loadUrl";
        }
      }
      const applicationId = item?.applicationId;
      const sceneId = item?.sceneId;
      if (applicationId && sceneId) {
        const badgeItem = thirdAppMsgCount[`${applicationId}_${sceneId}`];
        item.tip = badgeItem?.showBadge ? badgeItem?.badge || 0 : 0;
      }
      return item;
    },
    [thirdAppMsgCount]
  );

  // 更多展示
  const viewOtherLine = useMemo(() => {
    return [...viewOtherItems].map((item: any) => {
      const data = pcFuncInfoMap?.get?.(item?.name);
      const newItem = handleNavItem(item, data);
      const locked = lockKeys?.includes(item.name) || item.type === "add";
      const notRemove = notRemoveKeys?.includes(item.name) || item.type === "add";
      return (
        <SettingItem
          key={item.name}
          item={newItem}
          locked={locked}
          notRemove={notRemove}
          isEditing={false}
          itemType="extend"
          style={{ margin: "4px 6px" }}
        />
      );
    });
  }, [viewOtherItems, pcFuncInfoMap, handleNavItem, lockKeys, notRemoveKeys]);

  // 编辑数据
  // 基础显示数据
  const mainEditItems = useMemo(() => {
    let tNav = get(navPopInfo?.pcSide, "items");
    if (!tNav || !tNav.length) {
      tNav = [];
    }
    return tNav.slice(0, PC_SIDE_ITEM_NUM);
  }, [navPopInfo?.pcSide]);

  // 基础列
  const editMainLine = useMemo(() => {
    const isEditing = navPopInfo?.editing;
    return mainEditItems.map((item: any, index: number) => {
      const data = pcFuncInfoMap?.get?.(item?.name);
      const newItem = handleNavItem(item, data);
      const locked = lockKeys?.includes(item.name);
      const notRemove = notRemoveKeys?.includes(item.name);
      return isEditing && !locked ? (
        <Draggable key={item.name} isDragDisabled={locked} draggableId={item.name} index={index}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
              className="circular"
            >
              <SettingItem
                item={newItem}
                locked={locked}
                notRemove={notRemove}
                isEditing={isEditing}
              />
            </div>
          )}
        </Draggable>
      ) : (
        <SettingItem
          key={item.name}
          item={item}
          locked={locked}
          notRemove={notRemove}
          isEditing={isEditing}
        />
      );
    });
  }, [navPopInfo?.editing, mainEditItems, pcFuncInfoMap, handleNavItem, lockKeys, notRemoveKeys]);

  const editExtendItems = useMemo(() => {
    let tNav = get(navPopInfo?.pcSide, "items");
    let nav: any[] = [];
    if (tNav?.length) {
      nav = [...tNav];
    }
    return nav.slice(PC_SIDE_ITEM_NUM, PC_SIDE_ITEM_MAX);
  }, [navPopInfo?.pcSide]);

  // 编辑中所有选项
  const editAllItems = useMemo(() => {
    return [...mainEditItems, ...editExtendItems];
  }, [mainEditItems, editExtendItems]);

  // 扩展列
  const editExtendLine = useMemo(() => {
    const isEditing = navPopInfo?.editing;
    const nav = [...editExtendItems];
    if (isEditing && editAllItems.length < PC_SIDE_ITEM_MAX) {
      nav.push({
        type: "add",
        icon: "iconic_pc_amplify",
        role: "add"
      });
    }
    return nav.map((item: any, index: number) => {
      const data = pcFuncInfoMap?.get?.(item?.name);
      handleNavItem(item, data);
      const locked = lockKeys?.includes(item.name) || item.type === "add";
      const notRemove = notRemoveKeys?.includes(item.name) || item.type === "add";
      return (
        <Draggable key={item.name} isDragDisabled={locked} draggableId={item.name} index={index}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
              className="circular"
            >
              <SettingItem
                item={item}
                locked={locked}
                notRemove={notRemove}
                isEditing={isEditing}
                style={{ margin: "4px 6px" }}
              />
            </div>
          )}
        </Draggable>
      );
    });
  }, [editAllItems?.length, editExtendItems, lockKeys, notRemoveKeys, navPopInfo?.editing]);

  // 编辑
  const handleEdit = useCallback(() => {
    const pcSide = getPcSideConfig(teamConfig);
    const allItems = pcSide.items ? [...pcSide.items] : [...appRuntimeEnv.ui.layout.nav];
    pcSide.items = [...allItems];
    updateNavInfo({
      editing: true,
      pcSide: { ...pcSide }
    });
  }, [appRuntimeEnv?.ui?.layout?.nav, teamConfig, updateNavInfo]);

  // 确认
  const handleConfirm = useCallback(() => {
    const { pcSide } = navPopInfo || {};
    try {
      if (pcSide) {
        let pcItems = getPcSideConfig(teamConfig);
        pcItems = {
          ...pcItems,
          ...pcSide
        };
        const strSide = JSON.stringify(pcItems);
        updateTeamConfig({
          customPcSidebar: strSide
        });
        updateNavInfo({
          editing: false,
          pcSide: {},
          clear: false
        });
      }
    } catch (e) {}
  }, [navPopInfo, teamConfig, updateNavInfo, updateTeamConfig]);

  // 恢复默认
  const handleReset = useCallback(async () => {
    // 弹出确认
    const res = await syncConfirmNew("是否确认恢复默认状态");
    if (!res) {
      return;
    }
    const { pcSide } = navPopInfo || {};
    const defaultPcSider = getPcSideConfig(teamConfig, {
      pKey: PC_SIDEBAR_KEY,
      getDefault: false
    });
    pcSide.items = defaultPcSider.items || [...appRuntimeEnv.ui.layout.nav];
    updateTeamConfig({
      customPcSidebar: ""
    });
    updateNavInfo({
      editing: false,
      pcSide: {},
      clear: false
    });
  }, [appRuntimeEnv?.ui?.layout?.nav, navPopInfo, teamConfig, updateNavInfo, updateTeamConfig]);

  // 拖动
  const onDragEnd = useCallback(
    (result: any) => {
      setDragging(false);
      if (!result.source || !result.destination) {
        return;
      }
      const { index: startIndex, droppableId: originalId } = result.source;
      const { index: endIndex, droppableId: targetId } = result.destination;
      // 当没有改变排序时，不触发此方法。
      const pcSide = navPopInfo?.pcSide || {};
      if (!pcSide?.items) {
        pcSide.items = [];
      }
      let realEnd = endIndex;
      let realStart = startIndex;
      if (targetId === originalId) {
        if (originalId === "extendItems") {
          realEnd = endIndex + PC_SIDE_ITEM_NUM;
          realStart = startIndex + PC_SIDE_ITEM_NUM;
        }
      } else {
        if (originalId === "extendItems") {
          realStart = startIndex + PC_SIDE_ITEM_NUM;
        } else {
          realEnd = endIndex + PC_SIDE_ITEM_NUM;
        }
      }
      const [removed] = pcSide["items"].splice(realStart, 1);
      // 将原来的元素添加进数组
      pcSide["items"].splice(realEnd, 0, removed);
      updateNavPopInfo({
        pcSide: { ...pcSide }
      });
    },
    [navPopInfo?.pcSide, updateNavPopInfo]
  );

  const viewContent = useMemo(() => {
    return (
      <div className={styles["item-wrap"]}>
        {hasOthers ? (
          <div className={`${styles["line-wrap"]} ${styles["other"]}`}>
            <div className={styles["line"]}>{viewOtherLine}</div>
          </div>
        ) : (
          <Empty text="暂未设置更多菜单，请点击编辑进行设置" noImage={true} />
        )}
      </div>
    );
  }, [viewOtherLine, hasOthers]);

  const editContent = useMemo(() => {
    return (
      <DragDropContext
        onDragEnd={onDragEnd}
        onBeforeDragStart={() => {
          setDragging(true);
        }}
      >
        <div className={styles["item-wrap"]}>
          <Droppable droppableId="items">
            {(provided, snapshot) => (
              <div
                className={`${styles["line-wrap"]} ${styles["main"]} ${styles["edit"]}`}
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {isDragging ? null : <span className={styles["title"]}>标准导航栏</span>}
                <div className={styles["line"]}>{editMainLine}</div>
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          <Droppable droppableId="extendItems">
            {(provided, snapshot) => (
              <div
                className={`${styles["line-wrap"]}  ${styles["other"]} ${styles["edit"]}`}
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {isDragging ? null : <span className={styles["title"]}>扩展导航栏</span>}
                <div className={styles["line"]}>{editExtendLine}</div>
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
    );
  }, [editMainLine, onDragEnd, editExtendLine]);

  const contentBody = useMemo(() => {
    return (
      <div
        className={`${styles["content"]} ${!navPopInfo?.editing ? styles["view"] : styles["edit"]}`}
      >
        <div className={styles["header"]}>
          {needUpdate ? (
            <div className={styles["tip"]}>
              <UpgradeTipTag />
            </div>
          ) : null}
          {!navPopInfo?.editing ? (
            <>
              <span className={styles["text"]}>更多功能/应用</span>
              <span className={styles["btn"]} onClick={handleEdit}>
                编辑
              </span>
            </>
          ) : (
            <>
              <div>
                <span className={styles["text"]}>可拖动调整排序</span>
                <span className={styles["btn"]} onClick={handleReset}>
                  恢复默认
                </span>
              </div>
              <div>
                <span
                  className={styles["btn-block"]}
                  onClick={() =>
                    updateNavInfo({
                      editing: false,
                      pcSide: {}
                    })
                  }
                >
                  取消
                </span>
                <span
                  className={`${styles["btn-block"]} ${styles["primary"]}`}
                  onClick={handleConfirm}
                >
                  确定
                </span>
              </div>
            </>
          )}
        </div>
        {navPopInfo?.editing ? editContent : viewContent}
      </div>
    );
  }, [
    editContent,
    handleConfirm,
    handleEdit,
    handleReset,
    navPopInfo?.editing,
    updateNavInfo,
    viewContent
  ]);
  // 设置浮层
  const popperBody = useMemo(() => {
    const wHeight = document.body.clientHeight;
    const maxTop = wHeight - 20 - 440;
    const minTop = navPopInfo?.top - 390 < 0 ? navPopInfo?.top - 92 : navPopInfo?.top - 390;
    return (
      <div
        className={styles["nav-setting-popper"]}
        style={{
          display: navPopInfo?.visible ? "" : "none",
          left: navPopInfo?.left,
          top: maxTop < navPopInfo?.top ? maxTop : minTop
        }}
      >
        {contentBody}
      </div>
    );
  }, [contentBody, navPopInfo?.left, navPopInfo?.top, navPopInfo?.visible]);

  return (
    <>
      <div>
        {navPopInfo?.visible && (
          <div
            onClick={hidePop}
            style={{
              position: "fixed",
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              zIndex: 100
            }}
          />
        )}
        {popperBody}
      </div>
      {navPopInfo?.appModalShow ? (
        <ThirdAppListModal selects={editAllItems} locks={notRemoveKeys} />
      ) : null}
    </>
  );
}

function mapStateToProps({
  user,
  ebook,
  thirdapp
}: {
  user: UserState;
  ebook: EbookState;
  thirdapp: ThirdAppState;
}) {
  const { thirdAppMsgCount, thirdAppMap } = thirdapp;
  const thirdAppMsgTotalCount: number = Object.keys(thirdAppMsgCount).reduce((prev, current) => {
    let _prev = prev;
    _prev =
      thirdAppMap[current] !== undefined
        ? _prev + (thirdAppMsgCount[current]?.showBadge ? thirdAppMsgCount[current]?.badge || 0 : 0)
        : _prev;
    return _prev;
  }, 0);
  return {
    navPopInfo: user.navPopInfo,
    teamConfig: user.teamConfig,
    newContactsCount: ebook.newContactsCount,
    pcFuncInfoMap: user.pcFuncInfoMap,
    thirdAppMsgCount: thirdapp.thirdAppMsgCount,
    thirdAppMsgTotalCount
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    updateNavPopInfo(payload: any) {
      dispatch({ type: "user/updateNavPopInfo", payload });
    },
    updateTeamConfig(payload: any) {
      dispatch({ type: "user/updateTeamConfig", payload });
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(NavSetting);
